import axios from 'axios'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import type { SagaReturnType } from 'redux-saga/effects'
import { StatusCodes } from 'http-status-codes'
import * as Sentry from '@sentry/nextjs'

import {
  setPackages as setPackagesAction,
  paymentFail,
  paymentSuccess,
  REBUILD,
  START_PAYMENT,
} from '../../actions/cart/cart'
import { START_PAYMENT as START_WEBSHOP_PAYMENT } from '../../actions/webshop/webshop'
import { showDialog } from '../../actions/ui/ui'
import finalizePayment from '../../../service/payment/finalize-payment/finalizePayment'
import startPayment from '../../../service/payment/start-payment/startPayment'
import startWebshopPayment from '../../../service/webshop/start-payment/startPayment'
import getBasket from '../../../service/webshop/get-basket/getBasket'
import { getApiUrl, PAYMENT_API_URL } from '../../../service/util/api/api'
import getProducts from '../../../service/products/getProducts'
import navigateToPage from '../../../util/navigate-to-page/navigateToPage'

import type { RebuildCartAction, StartPaymentAction } from '../../actions/cart/cart'
import { unexpectedError, unexpectedResponse } from '../../../util/show-error-dialog/showErrorDialog'
import { selectCart } from '../../selectors/cart/cart'

import type { StartPaymentAction as StartWebshopPaymentAction } from '../../actions/webshop/webshop'
import { TAX } from '../../../components/cart/Cart/Cart'
import { PaymentMethod, PaymentParams } from '../../../schema/payment'
import { DisplayOption } from '../../../schema/webshop-package'
import { BasketItem } from '../../../schema/basket'
import { InitiatedPayment } from '../../../pages/api/payment/init'
import normalizePaymentMethod from '../../../util/normalizePaymentMethod'

type PaymentData = {
  basketId: string
  draftId?: string
  basketItems: Array<BasketItem>
  paymentMethod: PaymentMethod
  paymentParams?: PaymentParams
  checksum: string
}

export const paymentMethodToPpspPaymentMethod = {
  ideal: 'IDEAL',
  mastercard: 'MASTERCARD',
  visa: 'VISA'
} as const

export const locationAssign = (href: string) => global.window.location.assign(href)

export function* initPspSaga(params: PaymentData) {
  const { basketId, draftId, basketItems, paymentMethod, paymentParams, checksum } = params

  const basketTotalPrice = basketItems.reduce((acc, basketItem) => acc + basketItem.price, 0)
  // Comma-delimited string of productCodes for all individual products in the basket (e.g. x,x,y,y,y,z,z)
  const complus: string = basketItems
    .map((basketItem: BasketItem) => new Array(basketItem.amount).fill(basketItem.product.productCode))
    .join(',')

  const basketTotalPriceIncludingBTW = Math.round(basketTotalPrice * 100 * (1 + TAX))
  const feedbackUrl = `${window.location.origin}/dashboard/payment-validate/${draftId || ''}`

  global.document.cookie = `pspBasketId=${basketId};path=/;secure;samesite`

  try {
    const { data: { redirectUrl } } = yield axios.post<InitiatedPayment>(
      getApiUrl(`${PAYMENT_API_URL}/init`),
      {
        orderId: basketId,
        amount: basketTotalPriceIncludingBTW / 100,
        paymentMethod: paymentMethodToPpspPaymentMethod[paymentMethod],
        callbackUrl: `${feedbackUrl}?COMPLUS=${encodeURIComponent(complus)}&orderID=${encodeURIComponent(basketId)}`,
        checksum
      },
      {
        withCredentials: true
      }
    )

    yield call(locationAssign, redirectUrl)
  } catch (error) {
    yield call(Sentry.captureException, error)
    yield put(paymentFail())
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: 'Voorbereiden van betaalgegevens mislukt. Probeer het later opnieuw.',
      }),
    )
  }
}

export function* getJPFStartPaymentData(action: StartPaymentAction) {
  const { draftId, cartItems, paymentMethod, paymentParams, poNumber, invoiceAddress } = action.payload
  const packageCodes = cartItems.map(cartItem => cartItem.product.productCode)
  const response = yield call(startPayment, packageCodes, normalizePaymentMethod(paymentMethod), paymentParams, poNumber, invoiceAddress)

  if (response.status === StatusCodes.OK) {
    const { basketId, checksum } = response.data

    return yield {
      basketId,
      draftId,
      basketItems: cartItems,
      paymentMethod,
      paymentParams,
      checksum
    }
  }

  return null
}

export function* getWebshopStartPaymentData(action: StartWebshopPaymentAction) {
  const { paymentMethod, paymentParams, poNumber, invoiceAddress } = action.payload
  const { data: getBasketResponse, status: getBasketStatus }: SagaReturnType<typeof getBasket> =
    yield call<any>(getBasket)

  const { data: { basketId, checksum }, status: startPaymentStatus } = yield call(
    startWebshopPayment,
    normalizePaymentMethod(paymentMethod),
    paymentParams,
    poNumber,
    invoiceAddress,
  )

  if (startPaymentStatus === StatusCodes.OK && getBasketStatus === StatusCodes.OK) {
    return yield { basketItems: getBasketResponse.items, basketId, paymentMethod, paymentParams, checksum }
  }

  return null
}

export function* getStartPaymentData(action: StartPaymentAction | StartWebshopPaymentAction) {
  try {
    let response

    if (action.type === START_PAYMENT) {
      response = yield call(getJPFStartPaymentData, action)
    } else {
      response = yield call(getWebshopStartPaymentData, action)
    }

    if (response) {
      return yield response
    }

    yield put(unexpectedResponse())
    yield put(paymentFail())
  } catch (error) {
    // yield call(Sentry.captureException, error)
    yield put(unexpectedError(error))
    yield put(paymentFail())
  }

  return null
}

export function* finalizePaymentSaga(basketId: string, context: string, paymentMethod: string) {
  try {
    const response = yield call(finalizePayment, basketId)

    if (response.status === StatusCodes.OK) {
      yield call(navigateToPage, `/${context}/cart/success`, undefined, { shallow: true })
      yield put(paymentSuccess({ basketId, context, paymentMethod }))
    } else {
      yield call(navigateToPage, `/${context}/cart/error`, undefined, { shallow: true })
      yield put(unexpectedResponse())
      yield put(paymentFail())
    }
  } catch (error) {
    // yield call(Sentry.captureException, error)
    yield put(unexpectedError(error))
    yield put(paymentFail(error.message))
  }
}

export function* startPaymentSaga(action: StartPaymentAction | StartWebshopPaymentAction) {
  const startPaymentData = yield call(getStartPaymentData, action)

  if (!startPaymentData) return

  const { paymentMethod, basketId } = startPaymentData

  if (['creditcard', 'mastercard', 'visa', 'ideal'].includes(paymentMethod)) {
    yield call(initPspSaga, startPaymentData)
  } else {
    const context = action.type === START_PAYMENT ? 'vacatures' : 'webshop'

    yield call(finalizePaymentSaga, basketId, context, paymentMethod)
  }
}

export function* rebuildCartSaga({ payload: { paymentAction, productCodes } }: RebuildCartAction) {
  try {
    const response: SagaReturnType<typeof getProducts> = yield call(getProducts, [
      DisplayOption.combined,
      DisplayOption.jpf,
    ])

    if (response.status === StatusCodes.OK && response.data) {
      const packages = response.data.filter(webshopPackage => productCodes.includes(webshopPackage.productCode))

      yield put(setPackagesAction({ packages }))

      if (paymentAction) {
        yield put(paymentAction)
      }

      const {
        payment: { status },
      } = yield select(selectCart)

      yield call(navigateToPage, `/vacatures/cart/${status}`, undefined, { shallow: true })
    } else {
      yield put(unexpectedResponse())
    }
  } catch (error) {
    // yield call(Sentry.captureException, error)
    yield put(unexpectedError(error))
  }
}

export default function* saga() {
  yield all([
    takeLatest([START_PAYMENT, START_WEBSHOP_PAYMENT], startPaymentSaga),
    takeLatest(REBUILD, rebuildCartSaga),
  ])
}
